/**
 * Account + platform-config seam: the current user's device sessions, MFA
 * factors, notifications; and platform-wide config such as currencies, payment
 * gateways, broadcasts, and batch settings. Wraps the clientBatch mock consts
 * behind hooks so the mock→real flip is an env change (VITE_USE_MOCKS).
 *
 * Pure business-logic helpers (gatewaysForCurrency) and fixed config constants
 * (SESSION_CAP_*) are re-exported here so pages import them from the seam too,
 * never from mocks/ directly. The OTP/TOTP `verify*` functions branch the same
 * way every data resource does: mock-compare today, POST to the API on flip.
 */
import { useQuery } from '@tanstack/react-query';
import { USE_MOCKS, mockDelay, apiGet, apiGetList, apiPost } from './client';
import {
  currencies,
  paymentGatewaysDetailed,
  myActiveSessions,
  my2faFactors,
  my2faRecoveryCodes,
  myNotifications,
  broadcasts,
  platformBatchSettings,
  mockPrimary2faFactor,
  pendingModalBroadcasts,
  MOCK_VALID_OTP,
  MOCK_VALID_TOTP,
  type AppNotification,
} from '../mocks/clientBatch';

/* ── Resource fetchers ───────────────────────────────────────────────────── */

export const listCurrencies = () =>
  USE_MOCKS ? mockDelay(currencies) : apiGetList<(typeof currencies)[number]>('/currencies');
export const listPaymentGateways = () =>
  USE_MOCKS ? mockDelay(paymentGatewaysDetailed) : apiGetList<(typeof paymentGatewaysDetailed)[number]>('/admin/gateways');
export const listMyActiveSessions = () =>
  USE_MOCKS ? mockDelay(myActiveSessions) : apiGetList<(typeof myActiveSessions)[number]>('/me/sessions');
export const listMy2faFactors = () =>
  USE_MOCKS ? mockDelay(my2faFactors) : apiGetList<(typeof my2faFactors)[number]>('/me/2fa/factors');
export const listMy2faRecoveryCodes = () =>
  USE_MOCKS ? mockDelay(my2faRecoveryCodes) : apiGetList<(typeof my2faRecoveryCodes)[number]>('/me/2fa/recovery-codes');
export const getMfaPrimaryFactor = () =>
  USE_MOCKS ? mockDelay(mockPrimary2faFactor) : apiGet<typeof mockPrimary2faFactor>('/me/2fa/primary');
export const listMyNotifications = () =>
  USE_MOCKS ? mockDelay(myNotifications) : apiGetList<(typeof myNotifications)[number]>('/me/notifications');
export const listBroadcasts = () =>
  USE_MOCKS ? mockDelay(broadcasts) : apiGetList<(typeof broadcasts)[number]>('/admin/broadcasts');
// User-facing banner feed — every role can read this (the /admin one is platform-only).
export const listMyBroadcasts = () =>
  USE_MOCKS ? mockDelay(broadcasts) : apiGetList<(typeof broadcasts)[number]>('/me/broadcasts');
export const getPlatformBatchSettings = () =>
  USE_MOCKS ? mockDelay(platformBatchSettings) : apiGet<typeof platformBatchSettings>('/admin/settings');
export const getPendingModalBroadcasts = () =>
  USE_MOCKS ? mockDelay(pendingModalBroadcasts()) : apiGetList<AppNotification>('/me/notifications/modal');

/* ── Query hooks ─────────────────────────────────────────────────────────── */

export const useCurrencies = () => useQuery({ queryKey: ['currencies'], queryFn: listCurrencies });
export const usePaymentGateways = () =>
  useQuery({ queryKey: ['admin', 'gateways'], queryFn: listPaymentGateways });
export const useMyActiveSessions = () =>
  useQuery({ queryKey: ['me', 'sessions'], queryFn: listMyActiveSessions });
export const useMy2faFactors = () => useQuery({ queryKey: ['me', '2fa', 'factors'], queryFn: listMy2faFactors });
export const useMy2faRecoveryCodes = () =>
  useQuery({ queryKey: ['me', '2fa', 'recovery'], queryFn: listMy2faRecoveryCodes });
export const useMfaPrimaryFactor = () =>
  useQuery({ queryKey: ['me', '2fa', 'primary'], queryFn: getMfaPrimaryFactor });
export const useMyNotifications = () =>
  useQuery({ queryKey: ['me', 'notifications'], queryFn: listMyNotifications });
export const useBroadcasts = () => useQuery({ queryKey: ['admin', 'broadcasts'], queryFn: listBroadcasts });
export const useMyBroadcasts = () => useQuery({ queryKey: ['me', 'broadcasts'], queryFn: listMyBroadcasts });
export const usePlatformBatchSettings = () =>
  useQuery({ queryKey: ['admin', 'settings'], queryFn: getPlatformBatchSettings });
export const usePendingModalBroadcasts = () =>
  useQuery({ queryKey: ['me', 'notifications', 'modal'], queryFn: getPendingModalBroadcasts });

/* ── Auth challenge verification ─────────────────────────────────────────────
 * Mock mode compares against the demo constant; on flip these POST to the API
 * and the constants drop out entirely (the backend owns the real check). */

export const verifyOtp = (code: string): Promise<boolean> =>
  USE_MOCKS
    ? mockDelay(code === MOCK_VALID_OTP)
    : apiPost<{ ok: boolean }>('/auth/verify-otp', { code }).then((r) => r.ok);

export const verifyTotp = (code: string): Promise<boolean> =>
  USE_MOCKS
    ? mockDelay(code === MOCK_VALID_TOTP)
    : apiPost<{ ok: boolean }>('/auth/mfa/verify', { code }).then((r) => r.ok);

/* ── Pure helpers / fixed config (re-exported so pages never touch mocks/) ─── */

export {
  gatewaysForCurrency,
  SESSION_CAP_DEFAULT,
  SESSION_CAP_MAX,
  SESSION_CAP_MIN,
} from '../mocks/clientBatch';
